import './styles.css';
import { collectCapabilities } from './lab/capabilities';
import { runSmoke, type CancellationCheckpoint, type SmokeRunOptions } from './lab/smoke';
import { runL3Probe } from './lab/l3-probe';
import { createEvidenceArchive } from './lab/evidence-archive';
import { OpfsWorkspace } from './lab/workspace';
import type { L3Report } from './lab/l3-report';
import type { SmokeProgress, SmokeReport, SmokeResult } from './lab/types';

type View = 'run' | 'observe' | 'reports';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) {
  throw new Error('Application root is missing.');
}
const root = app;

let activeView: View = 'run';
let latestResult: SmokeResult | null = null;
let latestL3Report: L3Report | null = null;
let running = false;
let progress: SmokeProgress | null = null;
let shareMessage: string | null = null;
let smokeAbortController: AbortController | null = null;

declare global {
  interface Window {
    __BITPOP_LAB_TEST__?: {
      runSmoke: typeof runSmoke;
      inventory: () => ReturnType<OpfsWorkspace['inventory']>;
      holdWorkspaceLock: (milliseconds: number) => Promise<void>;
    };
  }
}

function download(file: File): void {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function downloadReport(): void {
  if (!latestResult) {
    return;
  }
  const report = new File(
    [JSON.stringify(latestResult.report, null, 2)],
    `${latestResult.report.runId}.json`,
    { type: 'application/json' },
  );
  download(report);
}

async function downloadEvidenceArchive(): Promise<void> {
  const archive = await createEvidenceArchive(latestResult, latestL3Report);
  if (archive) download(archive);
}

function canShareArtifact(file: File): boolean | null {
  return typeof navigator.canShare === 'function'
    ? navigator.canShare({ files: [file] })
    : null;
}

function shareFailureMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'Partage annulé.';
  }
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return 'Chrome/Android a refusé le partage direct. Téléchargez le WebM, puis partagez-le depuis Téléchargements pour isoler le refus navigateur.';
  }
  return `Échec du partage : ${error instanceof Error ? error.message : String(error)}`;
}

function shareArtifact(): void {
  if (!latestResult?.artifact) {
    return;
  }
  if (typeof navigator.share !== 'function') {
    shareMessage = 'Web Share est indisponible. Téléchargez le WebM pour le transférer.';
    render();
    return;
  }
  if (canShareArtifact(latestResult.artifact) === false) {
    shareMessage = 'Ce navigateur refuse ce WebM pour le partage direct. Téléchargez-le puis partagez-le depuis Téléchargements.';
    render();
    return;
  }
  // Keep navigator.share() in the synchronous click path: Web Share consumes the
  // transient user activation that Android grants to this button press.
  void navigator.share({ files: [latestResult.artifact], title: 'BitPop S0 WebM VP9/Opus' })
    .then(() => {
      shareMessage = 'Feuille de partage ouverte.';
      render();
    })
    .catch((error: unknown) => {
      shareMessage = shareFailureMessage(error);
      render();
    });
}

function renderJson(value: unknown): string {
  return JSON.stringify(value, null, 2).replaceAll('&', '&amp;').replaceAll('<', '&lt;');
}

function render(): void {
  const active = (view: View): string => view === activeView ? 'aria-current="page"' : '';
  const body = activeView === 'run' ? renderRun() : activeView === 'observe' ? renderObserve() : renderReports();
  root.innerHTML = `
    <main>
      <header>
        <p class="eyebrow">BitPop · laboratoire L0/L4</p>
        <h1>Faisabilité PWA 480p</h1>
        <p class="subtle">Commit ${__BITPOP_COMMIT__} · arbre ${__BITPOP_TREE_STATE__}</p>
      </header>
      <nav aria-label="Vues du laboratoire">
        <button data-view="run" ${active('run')}>Exécuter</button>
        <button data-view="observe" ${active('observe')}>Observer</button>
        <button data-view="reports" ${active('reports')}>Rapports</button>
      </nav>
      ${body}
    </main>`;
  bindEvents();
}

function renderRun(): string {
  const result = latestResult;
  const artifactShareCapability = result?.artifact ? canShareArtifact(result.artifact) : null;
  const progressView = running && progress ? `
    <section class="progress-panel" aria-live="polite">
      <h2>Progression</h2>
      <p>${progress.message}</p>
      ${progress.completed !== null && progress.total !== null ? `<progress value="${progress.completed}" max="${progress.total}"></progress><p>${progress.completed}/${progress.total}</p>` : '<div class="spinner" aria-label="Traitement en cours"></div>'}
      ${smokeAbortController ? '<button data-action="cancel-smoke">Annuler l’export</button>' : ''}
    </section>` : '';
  const outcome = result ? `
    <section class="result">
      <h2>Dernier résultat : ${result.report.status}</h2>
      <p>${result.report.errors.length === 0 ? 'Le WebM attend une validation FFmpeg/ffprobe indépendante.' : 'Le rapport contient un échec structuré.'}</p>
      ${result.artifact ? '<button data-action="download-artifact">Télécharger le WebM</button><button data-action="share-artifact">Partager le WebM</button>' : ''}
      <button data-action="download-report">Télécharger le rapport JSON</button>
      ${result.artifact ? `<p class="notice">Partage direct du WebM : ${artifactShareCapability === true ? 'pris en charge par le navigateur' : artifactShareCapability === false ? 'refusé par le navigateur' : 'capacité non exposée par ce navigateur'}.</p>` : ''}
      ${shareMessage ? `<p class="notice">${shareMessage}</p>` : ''}
    </section>` : '';
  const l3Outcome = latestL3Report ? `
    <section class="result">
      <h2>Dernier L3 : ${latestL3Report.status}</h2>
      <p>${latestL3Report.fixtures.length} fixtures vérifiées, ${latestL3Report.errors.length} erreur(s) structurée(s).</p>
      <button data-action="download-l3-report">Télécharger le rapport L3</button>
    </section>` : '';
  const evidenceArchive = latestResult || latestL3Report ? `
    <section class="result">
      <h2>Paquet de preuve</h2>
      <p>Rapports persistés, artefacts OPFS <code>READY</code> et preuves de session, avec manifeste SHA-256.</p>
      <button data-action="download-evidence-archive">Télécharger le paquet d’audit ZIP</button>
    </section>` : '';
  return `
    <section>
      <h2>S0-WEBM-OPUS-5S</h2>
      <p>120 trames synthétiques 480×480 à 24 fps, PCM stéréo 48 kHz et WebM VP9/Opus. C’est le contrat codec/mux du prototype. Depuis L4, ce probe publie aussi son artefact de façon transactionnelle dans OPFS.</p>
      <button class="primary" data-action="run-smoke" ${running ? 'disabled' : ''}>${running ? 'Encodage en cours…' : 'Lancer le probe VP9/Opus'}</button>
      <p class="notice">Lancez depuis un geste utilisateur. Téléchargez ensuite le WebM et le rapport, comparez les SHA-256, puis validez le fichier avec <code>npm run validate:webm</code>.</p>
    </section>
    <section>
      <h2>L4 — OPFS, publication et annulation</h2>
      <p>Écrit le WebM progressivement dans OPFS, vérifie sa structure et son hash, puis crée le marqueur <code>READY</code> en dernier. Les essais d’annulation ne doivent publier aucun fichier.</p>
      <button class="primary" data-action="run-l4-success" ${running ? 'disabled' : ''}>${running ? 'Probe L4 en cours…' : 'Lancer le probe L4 — OPFS'}</button>
      <p class="notice">Annulations reproductibles :</p>
      <button data-action="run-l4-cancel" data-checkpoint="CANCEL_RENDER" ${running ? 'disabled' : ''}>Annuler après rendu (image 36)</button>
      <button data-action="run-l4-cancel" data-checkpoint="CANCEL_MUX" ${running ? 'disabled' : ''}>Annuler après première écriture</button>
      <button data-action="run-l4-cancel" data-checkpoint="CANCEL_PRE_READY" ${running ? 'disabled' : ''}>Annuler avant READY</button>
    </section>
    <section>
      <h2>L3 — corpus, rendu et audio</h2>
      <p>Décode les fixtures GIF/WebP dans un Worker, rend une trame 480p avec la police locale et vérifie les sélections audio 48 kHz. Ce probe ne produit aucun export.</p>
      <button data-action="run-l3" ${running ? 'disabled' : ''}>${running ? 'Probe en cours…' : 'Lancer le probe L3'}</button>
    </section>
    ${progressView}
    ${outcome}
    ${l3Outcome}
    ${evidenceArchive}`;
}

function renderObserve(): string {
  return `
    <section>
      <h2>Capacités de cette installation</h2>
      <pre>${renderJson(collectCapabilities())}</pre>
      ${latestResult ? `<h2>Configurations codec mesurées</h2><pre>${renderJson(latestResult.report.codecs)}</pre>` : '<p>Aucun spike n’a encore été exécuté.</p>'}
    </section>`;
}

function renderReports(): string {
  return `
    <section>
      <h2>Rapport brut</h2>
      ${latestResult ? `<h2>L1</h2><pre>${renderJson(latestResult.report)}</pre>` : ''}
      ${latestL3Report ? `<h2>L3</h2><pre>${renderJson(latestL3Report)}</pre>` : ''}
      ${latestResult || latestL3Report ? '' : '<p>Aucun rapport local. Les rapports téléchargés restent la preuve à archiver hors Git.</p>'}
    </section>`;
}

async function runSmokeFromUi(options: SmokeRunOptions, initialMessage: string): Promise<void> {
  running = true;
  const controller = new AbortController();
  smokeAbortController = controller;
  shareMessage = null;
  progress = { stage: 'capability-probe', message: initialMessage, completed: null, total: null };
  render();
  latestResult = await runSmoke({
    ...options,
    signal: controller.signal,
    onProgress: (nextProgress) => {
      progress = nextProgress;
      render();
    },
  });
  running = false;
  smokeAbortController = null;
  progress = null;
  render();
}

function bindEvents(): void {
  root.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => {
    button.addEventListener('click', () => {
      activeView = button.dataset.view as View;
      render();
    });
  });
  root.querySelector<HTMLButtonElement>('[data-action="run-smoke"]')?.addEventListener('click', async () => {
    await runSmokeFromUi({}, 'Démarrage du spike codec/mux…');
  });
  root.querySelector<HTMLButtonElement>('[data-action="run-l4-success"]')?.addEventListener('click', async () => {
    await runSmokeFromUi({}, 'Démarrage du probe L4 : écriture OPFS et publication READY…');
  });
  root.querySelectorAll<HTMLButtonElement>('[data-action="run-l4-cancel"]').forEach((button) => {
    button.addEventListener('click', async () => {
      const checkpoint = button.dataset.checkpoint as CancellationCheckpoint;
      await runSmokeFromUi({ cancelAt: checkpoint }, `Démarrage du checkpoint L4 : ${checkpoint}…`);
    });
  });
  root.querySelector<HTMLButtonElement>('[data-action="run-l3"]')?.addEventListener('click', async () => {
    running = true;
    progress = { stage: 'initializing', message: 'Exécution des fixtures L3…', completed: null, total: null };
    render();
    latestL3Report = (await runL3Probe()).report;
    try {
      const workspace = await OpfsWorkspace.open();
      await workspace.writeReport(latestL3Report.runId, latestL3Report);
    } catch {
      // The L3 probe remains reportable even when this browser has no OPFS.
    }
    running = false;
    progress = null;
    render();
  });
  root.querySelector<HTMLButtonElement>('[data-action="cancel-smoke"]')?.addEventListener('click', () => {
    smokeAbortController?.abort();
  });
  root.querySelector<HTMLButtonElement>('[data-action="download-artifact"]')?.addEventListener('click', () => {
    if (latestResult?.artifact) download(latestResult.artifact);
  });
  root.querySelector<HTMLButtonElement>('[data-action="download-report"]')?.addEventListener('click', downloadReport);
  root.querySelector<HTMLButtonElement>('[data-action="download-l3-report"]')?.addEventListener('click', () => {
    if (!latestL3Report) return;
    download(new File([JSON.stringify(latestL3Report, null, 2)], `${latestL3Report.runId}.json`, { type: 'application/json' }));
  });
  root.querySelector<HTMLButtonElement>('[data-action="download-evidence-archive"]')?.addEventListener('click', () => {
    void downloadEvidenceArchive();
  });
  root.querySelector<HTMLButtonElement>('[data-action="share-artifact"]')?.addEventListener('click', shareArtifact);
}

async function restoreLatestReadyArtifact(): Promise<void> {
  try {
    const workspace = await OpfsWorkspace.open();
    const inventory = await workspace.inventory();
    const exportId = inventory.readyExports.at(-1);
    if (!exportId || running) return;
    const [artifact, report] = await Promise.all([
      workspace.readReadyArtifact(exportId),
      workspace.readReport<SmokeReport>(exportId),
    ]);
    if (!artifact || !report || report.scenario.id !== 'S0-WEBM-OPUS-5S' || report.artifact.ready !== true
      || report.artifact.sha256 !== artifact.sha256) return;
    latestResult = { report, artifact: artifact.file };
    render();
  } catch {
    // OPFS is optional for opening the shell; an export run reports its own structured storage error.
  }
}

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js');
  });
}

if (new URLSearchParams(window.location.search).has('lab-test')) {
  window.__BITPOP_LAB_TEST__ = {
    runSmoke,
    inventory: async () => (await OpfsWorkspace.open()).inventory(),
    holdWorkspaceLock: async (milliseconds) => {
      const workspace = await OpfsWorkspace.open();
      await workspace.runExclusive(async () => {
        document.documentElement.dataset.bitpopWorkspaceLock = 'held';
        await new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
        delete document.documentElement.dataset.bitpopWorkspaceLock;
      });
    },
  };
}

render();
void restoreLatestReadyArtifact();
